import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TableAction, TableColumn } from '../../../shared/components/table/table.component';
import { BudgetServiceOrderTerciary } from '../../../models/BudgetServiceOrderTerciary';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { BudgetServiceOrdersService, BudgetServiceOrderSearchFilters } from '../../../services/budget-service-orders.service';
import { ServiceOrderWorkSectionsService } from '../../../services/service-order-work-sections.service';
import { BudgetCollaborationService } from '../../../services/budget-collaboration.service';
import { Appointment, SchedulesService } from '../../../services/schedules.service';
import {
  datetimeLocalToRecordedAtIso,
  defaultDatetimeLocalValue
} from '../../../shared/utils/action-recorded-at.util';

type BudgetListTab = 'orcamentos' | 'solicitacoes';

@Component({
  selector: 'app-budget-service-orders',
  templateUrl: './budget-service-orders.component.html',
  styleUrls: ['./budget-service-orders.component.css']
})
export class BudgetServiceOrdersComponent implements OnInit {
  budgets: BudgetServiceOrderTerciary[] = [];
  isLoading = false;
  activeTab: BudgetListTab = 'orcamentos';

  guinchoSolicitacoes: Appointment[] = [];
  loadingSolicitacoes = false;
  solicitacoesError: string | null = null;

  pageNumber = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 1;

  filterPlate = '';
  filterOsNumber = '';
  filterStatus = '';
  filterClientApproved: '' | 'true' | 'false' = '';

  statusOptions = [
    { value: '', label: 'Todos os Status' },
    { value: 'Pendente', label: 'Pendente' },
    { value: 'Aprovado', label: 'Aprovado' },
    { value: 'Em Revisão', label: 'Em Revisão' },
    { value: 'Serviço Realizado', label: 'Serviço Realizado' },
    { value: 'Cancelado', label: 'Cancelado' }
  ];

  clientApprovedOptions = [
    { value: '', label: 'Todos' },
    { value: 'true', label: 'Sim' },
    { value: 'false', label: 'Não' }
  ];

  approvingBudgetId: number | null = null;
  confirmingBudgetId: number | null = null;
  revisingBudgetId: number | null = null;

  showApproveRecordedAtModal = false;
  approveRecordedAt = '';
  approveRecordedAtError = '';
  pendingApproveBudget: BudgetServiceOrderTerciary | null = null;

  columns: TableColumn[] = [
    { titulo: 'Nº OS', label: 'osNumber' },
    { titulo: 'Execução', label: 'executionTypeLabel', formatter: (v: any) => v || '—' },
    { titulo: 'Placa', label: 'plateVechile' },
    { titulo: 'Oficina', label: 'workshopDisplayLabel', formatter: (v: any) => v || '—' },
    { titulo: 'Status', label: 'status' },
    {
      titulo: 'Aprovado pelo Cliente',
      label: 'clientApproved',
      formatter: (value: any) => (value ? 'Sim' : 'Não')
    }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Aprovar', icon: 'fas fa-check-circle', action: 'approve', class: 'btn-approve' }
  ];

  constructor(
    private router: Router,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService,
    private budgetService: BudgetServiceOrdersService,
    private workSectionsService: ServiceOrderWorkSectionsService,
    private budgetCollaboration: BudgetCollaborationService,
    private schedulesService: SchedulesService
  ) {}

  ngOnInit(): void {
    // Lista: um spinner (table). Badge de solicitações carrega em silêncio (sem 2º overlay).
    this.loadBudgets();
    this.loadGuinchoSolicitacoes({ showSpinner: false });
  }

  setTab(tab: BudgetListTab): void {
    this.activeTab = tab;
    if (tab === 'solicitacoes') {
      this.loadGuinchoSolicitacoes({ showSpinner: true });
    }
  }

  loadGuinchoSolicitacoes(options?: { showSpinner?: boolean }): void {
    const showSpinner = options?.showSpinner !== false;
    if (showSpinner) {
      this.loadingSolicitacoes = true;
    }
    this.solicitacoesError = null;
    this.schedulesService.getPartnerPendingAppointments().subscribe({
      next: (list) => {
        this.guinchoSolicitacoes = (list || []).filter(a =>
          !a.IsDeleted && this.isGuinchoSolicitacao(a)
        );
        if (showSpinner) {
          this.loadingSolicitacoes = false;
        }
      },
      error: () => {
        this.guinchoSolicitacoes = [];
        this.solicitacoesError = 'Não foi possível carregar as solicitações de guincho.';
        if (showSpinner) {
          this.loadingSolicitacoes = false;
        }
      }
    });
  }

  isGuinchoSolicitacao(app: Appointment): boolean {
    const subject = (app.Subject || '').toLowerCase();
    const description = (app.Description || '').toLowerCase();
    return subject.includes('solicitação guincho')
      || subject.includes('solicitacao guincho')
      || (subject.includes('guincho') && subject.includes('portal'))
      || description.includes('solicitação urgente de guincho')
      || description.includes('solicitacao urgente de guincho');
  }

  formatAppointmentDate(app: Appointment): string {
    const raw = (app.Date || '').toString();
    const day = raw.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      const [y, m, d] = day.split('-');
      return `${d}/${m}/${y}`;
    }
    return day || '—';
  }

  formatAppointmentTime(value: string | null | undefined): string {
    const t = (value || '').toString();
    if (t.length >= 5) return t.slice(0, 5);
    return t || '—';
  }

  canCreateBudgetFromGuincho(app: Appointment): boolean {
    return !!(app?.Id && app.B2BContractId && !app.ServiceOrderId);
  }

  criarOrcamentoDaSolicitacao(app: Appointment): void {
    if (!this.canCreateBudgetFromGuincho(app)) {
      this.errorService.showErrorMessage('Solicitação sem contrato B2B ou já vinculada a uma OS.');
      return;
    }
    const plate = (app.Plate || '').trim().toUpperCase();
    this.router.navigate(['/b2b/budget-service-orders/novo'], {
      queryParams: {
        appointmentId: app.Id,
        b2bContractId: app.B2BContractId,
        plate: plate || undefined,
        vehicleId: app.VehicleId || undefined,
        customerId: app.CustomerId || undefined,
        driverName: app.DriverName || undefined,
        isPartnerAppointment: 'true',
        fromGuincho: 'true'
      }
    });
  }

  loadBudgets(): void {
    this.isLoading = true;
    this.budgetService.getPaginated(this.pageNumber, this.pageSize, this.buildFilters()).subscribe({
      next: (res) => {
        this.budgets = this.decorateBudgets(res.items || []);
        this.totalItems = res.totalCount;
        this.pageNumber = res.page;
        this.pageSize = res.pageSize;
        this.totalPages = Math.max(1, res.totalPages);
        this.isLoading = false;
      },
      error: () => {
        this.budgets = [];
        this.totalItems = 0;
        this.totalPages = 1;
        this.isLoading = false;
      }
    });
  }

  private buildFilters(): BudgetServiceOrderSearchFilters {
    const filters: BudgetServiceOrderSearchFilters = {};
    if (this.filterPlate?.trim()) filters.plate = this.filterPlate.trim();
    if (this.filterOsNumber?.trim()) filters.osNumber = this.filterOsNumber.trim();
    if (this.filterStatus) filters.status = this.filterStatus;
    if (this.filterClientApproved === 'true') filters.clientApproved = true;
    else if (this.filterClientApproved === 'false') filters.clientApproved = false;
    return filters;
  }

  applyFilters(): void {
    this.pageNumber = 1;
    this.loadBudgets();
  }

  clearFilters(): void {
    this.filterPlate = '';
    this.filterOsNumber = '';
    this.filterStatus = '';
    this.filterClientApproved = '';
    this.pageNumber = 1;
    this.loadBudgets();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.filterPlate?.trim() ||
      this.filterOsNumber?.trim() ||
      this.filterStatus ||
      this.filterClientApproved
    );
  }

  onSearchChange(searchTerm: string): void {
    this.filterPlate = searchTerm?.trim() || '';
    this.pageNumber = 1;
    this.loadBudgets();
  }

  onPageChange(page: number): void {
    this.pageNumber = page;
    this.loadBudgets();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageNumber = 1;
    this.loadBudgets();
  }

  onActionClick(event: { action: string; item: BudgetServiceOrderTerciary }): void {
    const { action, item } = event;
    switch (action) {
      case 'view':
        this.openDetail(item);
        break;
      case 'approve':
        this.approveBudget(item);
        break;
      case 'confirmService':
        this.confirmService(item);
        break;
      case 'requestRevision':
        this.requestRevision(item);
        break;
      case 'edit':
        this.openEdit(item);
        break;
      case 'cancelBudget':
        this.cancelBudget(item);
        break;
    }
  }

  goToCreate(): void {
    this.router.navigate(['/b2b/budget-service-orders/novo']);
  }

  openDetail(budget: BudgetServiceOrderTerciary): void {
    if (!budget?.id) {
      this.errorService.showErrorMessage('Orçamento sem identificador.');
      return;
    }
    this.router.navigate(['/b2b/budget-service-orders', budget.id]);
  }

  openEdit(budget: BudgetServiceOrderTerciary): void {
    if (!budget?.id) return;
    this.router.navigate(['/b2b/budget-service-orders', budget.id, 'editar']);
  }

  cancelBudget(budget: BudgetServiceOrderTerciary): void {
    if (!budget?.id) return;

    this.confirmationService.confirm({
      title: 'Cancelar orçamento e OS',
      message: `Cancelar o orçamento da OS ${budget.osNumber || '#' + budget.id}? A ordem de serviço vinculada também será cancelada.`,
      confirmText: 'Cancelar orçamento',
      cancelText: 'Voltar',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-secondary',
      type: 'danger'
    }).subscribe(confirmed => {
      if (!confirmed || !budget?.id) return;
      this.budgetService.cancel(budget.id).subscribe({
        next: () => this.loadBudgets()
      });
    });
  }

  private buildRowActions(budget: BudgetServiceOrderTerciary): TableAction[] {
    const status = (budget?.status || '').toLowerCase();
    const isApproved = status === 'aprovado' || budget?.clientApproved === true;
    const isCompleted = status === 'serviço realizado' || status === 'servico realizado';
    const isInRevision = status === 'em revisão' || status === 'em revisao';
    const isCancelled = status === 'cancelado' || status === 'cancelada';

    const actions: TableAction[] = [
      { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' }
    ];

    if (!isCompleted && !isCancelled) {
      actions.push({ label: 'Editar', icon: 'fas fa-pen', action: 'edit', class: 'btn-edit' });
      actions.push({ label: 'Cancelar', icon: 'fas fa-ban', action: 'cancelBudget', class: 'btn-danger' });
    }

    if (!isApproved && !isCompleted && !isCancelled) {
      actions.push({ label: 'Aprovar', icon: 'fas fa-check-circle', action: 'approve', class: 'btn-approve' });
      if (!isInRevision) {
        actions.push({ label: 'Solicitar Revisão', icon: 'fas fa-rotate-left', action: 'requestRevision', class: 'btn-warning' });
      }
    } else if (isApproved && !isCompleted && !isCancelled) {
      actions.push({ label: 'Confirmar Serviço', icon: 'fas fa-clipboard-check', action: 'confirmService', class: 'btn-confirm-service' });
    }

    return actions;
  }

  private decorateBudgets(budgets: BudgetServiceOrderTerciary[]): BudgetServiceOrderTerciary[] {
    return (budgets || []).map(b => ({
      ...b,
      executionTypeLabel: b.externalServiceProviderName ? 'Parceiro' : 'Base',
      workshopDisplayLabel: b.externalServiceProviderName || 'Base',
      _actions: this.buildRowActions(b)
    } as any));
  }

  private mergeBudgetUpdate(
    current: BudgetServiceOrderTerciary,
    updated: BudgetServiceOrderTerciary
  ): BudgetServiceOrderTerciary {
    return {
      ...current,
      ...updated,
      externalServiceProviderId: updated.externalServiceProviderId ?? current.externalServiceProviderId,
      externalServiceProviderName: updated.externalServiceProviderName || current.externalServiceProviderName
    };
  }

  private updateWorkSectionStatus(workSectionId: number, status: string) {
    return this.workSectionsService.getById(workSectionId).pipe(
      switchMap((section) => {
        const payload: any = { status };
        const serviceOrderId = Number(section?.serviceOrderId || 0);
        if (serviceOrderId) payload.serviceOrderId = serviceOrderId;
        if (section?.description) payload.description = section.description;
        if (section?.externalServiceProviderId !== undefined && section?.externalServiceProviderId !== null) {
          payload.externalServiceProviderId = section.externalServiceProviderId;
        }
        return this.workSectionsService.update(workSectionId, payload).pipe(catchError(() => of(null)));
      }),
      catchError(() => of(null))
    );
  }

  private updateWorkSectionPaymentStatus(workSectionId: number, paymentStatus: string) {
    return this.workSectionsService.updateItemsPaymentStatus(workSectionId, paymentStatus);
  }

  approveBudget(budget: BudgetServiceOrderTerciary): void {
    if (!budget?.id) {
      this.errorService.showErrorMessage('Orçamento sem identificador.');
      return;
    }
    if (budget.clientApproved === true) {
      this.errorService.showErrorMessage('Este orçamento já está aprovado.');
      return;
    }

    this.pendingApproveBudget = budget;
    this.approveRecordedAt = defaultDatetimeLocalValue();
    this.approveRecordedAtError = '';
    this.showApproveRecordedAtModal = true;
  }

  closeApproveRecordedAtModal(): void {
    this.showApproveRecordedAtModal = false;
    this.approveRecordedAt = '';
    this.approveRecordedAtError = '';
    this.pendingApproveBudget = null;
  }

  confirmApproveBudget(): void {
    const budget = this.pendingApproveBudget;
    if (!budget?.id) return;

    const recordedAt = datetimeLocalToRecordedAtIso(this.approveRecordedAt);
    if (!recordedAt) {
      this.approveRecordedAtError = 'Informe uma data e hora válidas.';
      return;
    }

    this.approveRecordedAtError = '';
    this.showApproveRecordedAtModal = false;
    this.approvingBudgetId = budget.id!;
    this.pendingApproveBudget = null;

    this.budgetCollaboration.approveAll(budget.id!, recordedAt).subscribe({
      next: () => {
        this.approvingBudgetId = null;
        this.errorService.showSuccessMessage('Orçamento aprovado.');
        this.loadBudgets();
      },
      error: () => {
        this.approvingBudgetId = null;
      }
    });
  }

  confirmService(budget: BudgetServiceOrderTerciary): void {
    if (!budget?.id) {
      this.errorService.showErrorMessage('Orçamento sem identificador.');
      return;
    }

    this.confirmationService.confirm({
      title: 'Confirmar serviço',
      message: `Confirmar que o serviço do orçamento #${budget.id} foi realizado?`,
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-success',
      cancelButtonClass: 'btn-secondary',
      type: 'question'
    }).subscribe((confirmed) => {
      if (!confirmed) return;

      this.confirmingBudgetId = budget.id!;
      this.budgetService.update(budget.id!, {
        serviceOrderWorkSectionId: budget.serviceOrderWorkSectionId,
        osNumber: budget.osNumber,
        plateVechile: budget.plateVechile,
        status: 'Serviço Realizado',
        clientApproved: true
      }).pipe(
        switchMap((updated) => {
          const wsId = Number(budget.serviceOrderWorkSectionId || 0);
          if (!wsId) return of(updated);
          return this.updateWorkSectionStatus(wsId, 'Serviço Realizado').pipe(
            switchMap(() => this.updateWorkSectionPaymentStatus(wsId, 'PENDENTE')),
            map(() => updated)
          );
        })
      ).subscribe({
        next: (updated) => {
          this.confirmingBudgetId = null;
          this.refreshBudgetRow(budget.id!, updated);
          this.errorService.showSuccessMessage('Serviço confirmado.');
        },
        error: () => { this.confirmingBudgetId = null; }
      });
    });
  }

  requestRevision(budget: BudgetServiceOrderTerciary): void {
    if (!budget?.id) {
      this.errorService.showErrorMessage('Orçamento sem identificador.');
      return;
    }

    this.confirmationService.confirm({
      title: 'Solicitar revisão do orçamento',
      message: `Confirmar envio do orçamento #${budget.id} para revisão?`,
      confirmText: 'Solicitar Revisão',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-warning',
      cancelButtonClass: 'btn-secondary',
      type: 'warning'
    }).subscribe((confirmed) => {
      if (!confirmed) return;

      this.revisingBudgetId = budget.id!;
      this.budgetService.update(budget.id!, {
        serviceOrderWorkSectionId: budget.serviceOrderWorkSectionId,
        osNumber: budget.osNumber,
        plateVechile: budget.plateVechile,
        status: 'Em Revisão',
        clientApproved: false
      }).pipe(
        switchMap((updated) => {
          const wsId = Number(budget.serviceOrderWorkSectionId || 0);
          if (!wsId) return of(updated);
          return this.updateWorkSectionStatus(wsId, 'Orçamento-Em-Revisão').pipe(map(() => updated));
        })
      ).subscribe({
        next: (updated) => {
          this.revisingBudgetId = null;
          this.refreshBudgetRow(budget.id!, updated);
        },
        error: () => { this.revisingBudgetId = null; }
      });
    });
  }

  private refreshBudgetRow(budgetId: number, updated: BudgetServiceOrderTerciary): void {
    const idx = this.budgets.findIndex(b => b.id === budgetId);
    if (idx < 0) return;
    const merged = this.mergeBudgetUpdate(this.budgets[idx], updated);
    this.budgets[idx] = { ...merged, _actions: this.buildRowActions(merged) } as any;
    this.budgets = [...this.budgets];
  }
}
